import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Info, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDropzone } from "react-dropzone";
import { toastMessage, toastErrorMessage } from "@/utils/helper/myToast";
import { importEmployees } from "@/services/employee.services";
import Papa from "papaparse";
import Icon, { Image } from "@/common/ui/Image";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { checkIcon, crossIcon } from "@/dmr/common/SvgIcons";
import { TABLE } from "@/utils/constant/table.constant";
import CustomSelectField from "@/common/custom/CustomSelectField";
import { downloadFilteredCSV } from "@/utils/helper/import";
import { useNavigate } from "react-router-dom";

interface ImportEmployeeProps {
  onClose: () => void;
  afterCall?: () => void;
}

interface FileData {
  file: File;
  name: string;
  size: string;
  columns: string[];
  data: any[];
}

interface FieldMapping {
  fileColumn: string;
  lystlocField: string;
  mapped: boolean;
}

type ImportAction = "new" | "update" | "both";

const TruncateFileName: React.FC<{ name: string; maxLength?: number; className?: string }> = ({ name, maxLength = 32, className }) => {
  if (!name) return null;
  if (name.length <= maxLength) return <span className={className}>{name}</span>;
  const ext = name.slice(-5);
  const truncated = name.slice(0, maxLength - 5);
  return (
    <span className={className}>
      {truncated}...{ext}
    </span>
  );
};

const ImportEmployee: React.FC<ImportEmployeeProps> = ({ onClose, afterCall }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [fileData, setFileData] = useState<FileData | null>(null);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [importAction, setImportAction] = useState<ImportAction>("new");
  const [keyField, setKeyField] = useState<string>("");
  const [isImporting, setIsImporting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const navigate = useNavigate();

  // Step 1: File Upload
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && file.type === "text/csv") {
      setIsParsingFile(true);

      // Parse CSV file
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setIsParsingFile(false);

          if (results.errors.length > 0) {
            toastErrorMessage("Error parsing CSV file. Please check the format.");
            return;
          }

          const columns = results.meta.fields || [];
          // Clean imported values: remove ="..." and unquote if present
          const cleanImportedValue = (value: string) => {
            if (!value) return "";
            // Remove Excel protection wrapper
            if (/^=".*"$/.test(value)) {
              value = value.slice(2, -1);
            }
            // Remove surrounding quotes and unescape double quotes
            if (/^".*"$/.test(value)) {
              value = value.slice(1, -1).replace(/""/g, '"');
            }
            return value;
          };
          const rawData = results.data;
          const data = rawData.map((row: any) => Object.fromEntries(Object.entries(row).map(([key, value]) => [key, cleanImportedValue(String(value ?? ""))])));

          if (columns.length === 0) {
            toastErrorMessage("No columns found in CSV file.");
            return;
          }

          const sizeInKB = file.size / 1024;
          const formattedSize = sizeInKB < 1 ? `${sizeInKB.toFixed(2)} kB` : `${sizeInKB.toFixed(0)} kB`;

          setFileData({
            file,
            name: file.name,
            size: formattedSize,
            columns,
            data: data.slice(0, 5), // Show first 5 rows for preview
          });
          setCurrentStep(2);
        },
        error: (error) => {
          setIsParsingFile(false);
          toastErrorMessage("Error reading CSV file: " + error.message);
        },
      });
    } else {
      toastErrorMessage("Please upload a valid CSV file");
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
    maxFiles: 1,
    maxSize: 30 * 1024 * 1024, // 30MB
  });

  // Step 2: Field Mapping
  const initializeFieldMappings = useCallback(() => {
    if (!fileData) return;

    const mappings: FieldMapping[] = fileData.columns.map((column) => ({
      fileColumn: column,
      lystlocField:
        [...TABLE.EMPLOYEE_TABLE_FIELDS, ...TABLE.EMPLOYEE_TABLE_HIDDEN_FIELDS].find((field) => field.label.toLowerCase() === column.toLowerCase())?.id || "",
      mapped: [...TABLE.EMPLOYEE_TABLE_FIELDS, ...TABLE.EMPLOYEE_TABLE_HIDDEN_FIELDS].some((field) => field.label.toLowerCase() === column.toLowerCase()),
    }));

    setFieldMappings(mappings);
  }, [fileData]);

  React.useEffect(() => {
    if (currentStep === 2 && fileData && fieldMappings.length === 0) {
      initializeFieldMappings();
    }
  }, [currentStep, fileData, fieldMappings.length, initializeFieldMappings]);

  const updateFieldMapping = (index: number, lystlocField: string) => {
    const newMappings = [...fieldMappings];
    newMappings[index] = {
      ...newMappings[index],
      lystlocField,
      mapped: lystlocField !== "",
    };
    setFieldMappings(newMappings);
  };

  // const clearFieldMapping = (index: number) => {
  //   updateFieldMapping(index, "");
  // };

  const handleClearFileData = () => {
    setFileData(null);
    setFieldMappings([]);
    setCurrentStep(1);
  };

  // Get already used field IDs excluding the current field being edited
  const getDisabledOptions = (currentIndex: number) => {
    return fieldMappings.filter((mapping, index) => index !== currentIndex && mapping.mapped).map((mapping) => mapping.lystlocField);
  };

  const unmappedCount = fieldMappings.filter((m) => !m.mapped).length;

  // Check if all required fields are mapped
  const requiredFields = [...TABLE.EMPLOYEE_TABLE_FIELDS, ...TABLE.EMPLOYEE_TABLE_HIDDEN_FIELDS].filter((field: any) => field.isReq && !field?.hideFromImport);
  const unmappedRequiredFields = requiredFields.filter((reqField) => !fieldMappings.some((mapping) => mapping.lystlocField === reqField.id && mapping.mapped));
  const hasAllRequiredFieldsMapped = unmappedRequiredFields.length === 0;

  useEffect(() => {
    if (importAction === "new") {
      setKeyField("");
    }
  }, [importAction]);

  // Step 4: Import Process
  const handleImport = async () => {
    if (!fileData) return;

    setIsImporting(true);
    setUploadProgress(0);

    try {
      const importData = {
        file: fileData.file,
        fieldMappings: fieldMappings.filter((m) => m.mapped),
        importAction,
        ...(importAction !== "new" && { keyField }),
      };

      const res = await importEmployees(importData, (progress) => {
        setUploadProgress(progress);
      });
      const { message, isError } = res;
      toastMessage(message, isError, isError ? undefined : { link: "/home/employees/import-history", label: "import history", isExternal: false }, navigate);
      afterCall?.();
      onClose();
    } catch (error) {
      toastErrorMessage("Import failed. Please try again.");
    } finally {
      setIsImporting(false);
      setUploadProgress(0);
    }
  };
  const handleSampleCsvDownload = async () => {
    downloadFilteredCSV(true);
  };

  const stepVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
  };

  useEffect(() => {
    const element = document.getElementById("modal-title");
    if (element) {
      if (currentStep === 2) {
        element.innerHTML = "Field Mapping";
      } else {
        element.innerHTML = "Import Employees";
      }
    }
  }, [currentStep]);

  return (
    <AnimatePresence>
      <div className="h-full flex flex-col overflow-hidden">
        {/* Step Indicator */}
        <div className="flex items-center justify-center p-2 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center gap-2">
                <motion.div
                  initial={false}
                  animate={{
                    backgroundColor: step < currentStep ? "#dcfce7" : step === currentStep ? "#6F36BC1A" : "#f3f4f6",
                    color: step < currentStep ? "#16a34a" : step === currentStep ? "#6F36BC" : "#9ca3af",
                    scale: step === currentStep ? 1.1 : 1,
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="w-5 h-5 rounded-full flex items-center justify-center text-sm font-medium"
                >
                  {step < currentStep ? (
                    <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ duration: 0.4, ease: "easeOut" }}>
                      <Check className="size-4 text-green-600" />
                    </motion.div>
                  ) : (
                    <motion.span initial={false} animate={{ scale: step === currentStep ? 1.1 : 1 }} transition={{ duration: 0.2 }} className="text-10">
                      {step}
                    </motion.span>
                  )}
                </motion.div>
                {step < 4 && (
                  <motion.div
                    initial={false}
                    animate={{
                      backgroundColor: step < currentStep ? "#86efac" : "#e5e7eb",
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="w-6 h-px"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <AnimatePresence mode="wait">
          {/* Step 1: File Upload */}
          {currentStep === 1 && (
            <motion.div key="step1" variants={stepVariants} initial="hidden" animate="visible" exit="exit" className="p-4 h-[calc(100%-54px-37px-85px)]">
              <p className="text-text-default font-normal !leading-normal text-13 mb-2">
                Import Employees using your own file, or use our
                <Button variant="link" className="text-13 text-text-primary !p-1 !font-semibold " onClick={handleSampleCsvDownload}>
                  Sample CSV
                </Button>
              </p>

              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  isDragActive ? "border-blue-400 bg-brand-light" : "border-text-light3 bg-brand-light"
                } ${isParsingFile ? "pointer-events-none" : ""}`}
              >
                <input {...getInputProps()} disabled={isParsingFile} />

                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
                  {isParsingFile ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full"
                    />
                  ) : (
                    <Icon src="/icons/file-icon-csv.png" alt="file-icon" />
                  )}
                </div>
                <p className="text-13 font-medium text-text-default mb-1">
                  {isParsingFile ? "Processing your file..." : isDragActive ? "Drop your file here" : "Drag and drop your file here"}
                </p>
                <p className="text-10 text-gray-500 mb-4">Your file needs to be in .csv format with a size of 30MB.</p>
                <Button className="px-6 py-2" disabled={isParsingFile}>
                  {isParsingFile ? "Processing..." : "Browse file"}
                </Button>
              </div>

              <div className="mt-4 p-3 bg-primaryOpacity-5 rounded-lg border border-primaryOpacity-10">
                <div className="flex items-enter justify-center gap-3">
                  <Info className="size-5 text-lystproof-primary" />
                  <div>
                    <p className="text-13 text-text-default leading-normal">
                      Please check if your file has all the mandatory fields filled to import successfully.
                      <Tippy
                        content={
                          <div className=" ">
                            <div className="text-10 font-medium mb-2">REQUIRED FIELDS</div>
                            <ul className="text-10 space-y-1">
                              {[...TABLE.EMPLOYEE_TABLE_FIELDS, ...TABLE.EMPLOYEE_TABLE_HIDDEN_FIELDS]
                                .filter((field: any) => field?.isReq)
                                .map((field) => (
                                  <li key={field.id}>• {field.label}</li>
                                ))}
                            </ul>
                          </div>
                        }
                        interactive={true}
                        trigger="click"
                        placement="top"
                        hideOnClick={true}
                        theme="light"
                        arrow={true}
                        animation="fade"
                      >
                        <span className=" text-text-primary pl-1 cursor-pointer hover:underline">Click here to check</span>
                      </Tippy>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Field Mapping */}
          {currentStep === 2 && fileData && (
            <div key="step2" className="flex flex-col h-[calc(100%-54px-17px-85px)] overflow-y-auto">
              {unmappedCount > 0 && (
                <div className="flex items-center gap-2 p-2 px-3 bg-warningBg rounded mx-2 mt-2">
                  <Image src="/icons/warning-icon.svg" alt="alert-icon" className="size-5" />
                  <p className="text-13 text-text-default">
                    Map columns from your file to the right Lystproof fields. Your <span className="font-medium">{unmappedCount} unmapped columns</span> won't
                    be imported.
                  </p>
                </div>
              )}

              {unmappedRequiredFields.length > 0 && (
                <div className="flex items-center gap-2 p-2 px-3 mx-2 bg-warningBg rounded border border-red-200 my-2">
                  <Image src="/icons/warning-icon.svg" alt="alert-icon" className="size-5" />
                  <div className="text-13 text-text-default">
                    <p className="font-medium text-red-600 mb-1">Required fields missing:</p>
                    <p>
                      Please map the following required fields:{" "}
                      <span className="font-medium text-red-600">{unmappedRequiredFields.map((field) => field.label).join(", ")}</span>
                    </p>
                  </div>
                </div>
              )}

              <div className="flex-1 p-4 flex flex-col">
                <div className="grid grid-cols-[calc(40%-32px)_calc(50%-32px)_calc(20%-32px)] gap-4 py-4 text-13  text-text-default flex-shrink-0 font-semibold bg-white sticky top-0 z-10">
                  <div>File column</div>
                  <div>Lystproof field</div>
                  <div>Mapped</div>
                </div>

                <div className="overflow-y-auto">
                  {fieldMappings.map((mapping, index) => (
                    <div key={index} className="grid grid-cols-[calc(40%-32px)_calc(50%-32px)_calc(20%-32px)] gap-4 items-center py-3 border-b border-gray-100">
                      <div>
                        <p className="font-medium text-text-default text-13 !leading-normal">{mapping.fileColumn}</p>
                        <p className="text-text-default text-10 !leading-normal truncate line-clamp-2">{fileData.data[0]?.[mapping.fileColumn] || ""}</p>
                      </div>

                      <div className="relative">
                        <CustomSelectField
                          options={[...TABLE.EMPLOYEE_TABLE_FIELDS, ...TABLE.EMPLOYEE_TABLE_HIDDEN_FIELDS].filter((field: any) => !field?.hideFromImport)}
                          value={mapping.lystlocField}
                          onChange={(value) => updateFieldMapping(index, value)}
                          placeholder="Select field"
                          showSearch={true}
                          disabledOptions={getDisabledOptions(index)}
                          showClearIcon={true}
                        />
                      </div>

                      <div className="px-3">{mapping.mapped ? <div className="flex items-center gap-2 text-green-600">{checkIcon}</div> : crossIcon}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Select Action */}
          {currentStep === 3 && (
            <motion.div key="step3" variants={stepVariants} initial="hidden" animate="visible" exit="exit" className="p-4 h-[calc(100%-54px-17px-85px)]">
              {fileData && (
                <div className="mb-6 p-4 py-12 rounded-lg border-2 border-dashed border-text-light3 bg-brand-light">
                  <div className="flex mx-8 items-center gap-3 p-3 bg-gray-50 rounded-llg border">
                    <Image src="/icons/file-upload-icon.svg" alt="" className=" h-5" />
                    <div className="text-left w-full">
                      <TruncateFileName name={fileData.name} className="font-medium text-text-default text-13 max-w-[calc(100%-32px)] w-full" />
                      <p className="text-text-default text-10">{fileData.size}</p>
                    </div>
                    <button onClick={handleClearFileData} className="ml-auto p-1 hover:bg-gray-200 rounded">
                      <X className="size-4 text-gray-400" />
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-[11px] font-bold text-text-default mb-4">CHOOSE WHAT NEEDS TO BE UPDATED</h3>

                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="importAction"
                      value="new"
                      checked={importAction === "new"}
                      onChange={(e) => setImportAction(e.target.value as ImportAction)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div>
                      <p className="font-medium text-text-default text-13">Add as new record</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="importAction"
                      value="update"
                      checked={importAction === "update"}
                      onChange={(e) => setImportAction(e.target.value as ImportAction)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div>
                      <p className="font-medium text-text-default text-13">Update existing records only</p>
                    </div>
                  </label>

                  {importAction === "update" && (
                    <div className="ml-7 mt-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="mb-3">
                        <label className="block text-13 font-medium text-text-default mb-2">
                          Find existing records based on
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <CustomSelectField
                          options={TABLE.EMPLOYEE_TABLE_FIELDS.filter((field) => field.isKey)}
                          value={keyField}
                          onChange={(value) => setKeyField(value)}
                          placeholder="Select field"
                          showSearch={false}
                        />
                      </div>
                      {!keyField && <p className="text-red-500 text-10">* This field is required</p>}
                    </div>
                  )}

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="importAction"
                      value="both"
                      checked={importAction === "both"}
                      onChange={(e) => setImportAction(e.target.value as ImportAction)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div>
                      <p className="font-medium text-text-default text-13">Both</p>
                    </div>
                  </label>

                  {importAction === "both" && (
                    <div className="ml-7 mt-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="mb-3">
                        <label className="block text-13 font-medium text-text-default mb-2">
                          Find existing records based on
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <CustomSelectField
                          options={TABLE.EMPLOYEE_TABLE_FIELDS.filter((field) => field.isKey)}
                          value={keyField}
                          onChange={(value) => setKeyField(value)}
                          placeholder="Select field"
                          showSearch={false}
                        />
                      </div>
                      {!keyField && <p className="text-red-500 text-10">* This field is required</p>}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Ready to Import */}
          {currentStep === 4 && (
            <motion.div
              key="step4"
              variants={stepVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="p-4 h-[calc(100%-54px-17px-85px)] flex flex-col"
            >
              <div className="text-center border-text-light3 bg-brand-light  border-2 border-dashed rounded-lg p-5">
                <h3 className="text-13 text-text-default mb-4">Your file is ready to be import!</h3>

                {fileData && (
                  <div className="flex mx-8 items-center gap-3 p-3 bg-gray-50 rounded-llg border">
                    <Image src="/icons/file-upload-icon.svg" alt="" className=" h-5" />
                    <div className="text-left">
                      <p className="font-medium text-text-default text-13">{fileData.name}</p>
                      <p className="text-text-default text-10">{fileData.size}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Single Button Container - Always at Bottom */}
        {currentStep === 1 ? null : (
          <div className="flex justify-end gap-x-4 items-center p-4 border-t border-gray-200 flex-shrink-0">
            <>
              <Button variant="outline" onClick={() => setCurrentStep(Math.max(1, currentStep - 1))} disabled={currentStep === 1}>
                Previous
              </Button>
              {currentStep < 4 ? (
                <Button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={
                    (currentStep === 2 && (fieldMappings.filter((m) => m.mapped).length === 0 || !hasAllRequiredFieldsMapped)) ||
                    (currentStep === 3 && (importAction === "update" || importAction === "both") && !keyField) ||
                    isImporting
                  }
                >
                  Next
                </Button>
              ) : (
                <Button onClick={handleImport} disabled={isImporting} className="px-4">
                  {isImporting ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                      />
                      {uploadProgress > 0 && uploadProgress < 100
                        ? `Uploading... ${uploadProgress}%`
                        : uploadProgress === 100
                        ? "Processing..."
                        : "Importing..."}
                    </>
                  ) : (
                    "Import"
                  )}
                </Button>
              )}
            </>
          </div>
        )}
      </div>
    </AnimatePresence>
  );
};

export default ImportEmployee;
